"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, ArrowLeftRight, ArrowRight, History, RotateCw, ShoppingBag, Sparkles, TriangleAlert, Users } from "lucide-react";
import FitHex from "@/features/collections/detail/FitHex";
import { RowsSkeleton, TxLink, dateTime } from "@/features/collections/detail/parts";
import { DeskEmpty } from "@/features/home-v2/desk/Desk";
import { SecGlyph } from "@/features/home-v2/ornament";
import type { NftActivityItem } from "../types";
import { forToken, readActivityRow, type ActivityKind } from "../activityRow";
import { useNftActivity, useNftHolders } from "./useNftRecord";
import { withPercent } from "@/lib/percent";
type Props = {
  contractAddress: string;
  tokenId: string;
  lang: "fa" | "en";
};
const dayKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
function dayLabel(iso: string, locale: string, isFa: boolean) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return isFa ? "امروز" : "Today";
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return isFa ? "دیروز" : "Yesterday";
  return new Date(iso).toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long"
  });
}
const KIND: Record<ActivityKind, {
  fa: string;
  en: string;
  Icon: typeof ShoppingBag;
}> = {
  mint: {
    fa: "مینت",
    en: "Mint",
    Icon: Sparkles
  },
  sale: {
    fa: "فروش",
    en: "Sale",
    Icon: ShoppingBag
  },
  transfer: {
    fa: "انتقال",
    en: "Transfer",
    Icon: ArrowLeftRight
  }
};
function Who(t0) {
  const $ = _c(7);
  const {
    name,
    address
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = name ? <b>{name}</b> : null;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== address) {
    t2 = address ? <FitHex value={address} /> : <span>—</span>;
    $[2] = address;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className="vt-who">{t1}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * THE TOKEN'S SALES, as the collection page's timeline — the legacy tab's
 * query and its three outcomes kept apart: a failed load (with a retry) is
 * never shown as «no activity». Grouped by day; each sale is the market, the
 * price, seller → buyer, the time and the transaction.
 */
export function NftActivity(t0) {
  const $ = _c(39);
  const {
    contractAddress,
    tokenId,
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const locale = isFa ? "fa-IR" : "en-US";
  const nf = new Intl.NumberFormat(locale);
  let t2;
  if ($[2] !== contractAddress || $[3] !== lang || $[4] !== tokenId) {
    t2 = {
      contractAddress,
      tokenId,
      lang
    };
    $[2] = contractAddress;
    $[3] = lang;
    $[4] = tokenId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    activities: answered,
    isLoading,
    isError,
    isFetching,
    refetch
  } = useNftActivity(t2);
  const activities = forToken(answered, tokenId);
  const Toward = isFa ? ArrowLeft : ArrowRight;
  if (isLoading) {
    let t3;
    if ($[6] !== t) {
      t3 = t("\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0641\u0639\u0627\u0644\u06CC\u062A\u200C\u0647\u0627", "Loading activity");
      $[6] = t;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t3) {
      t4 = <RowsSkeleton label={t3} rows={4} />;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (isError) {
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TriangleAlert />;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t) {
      t4 = t("\u0641\u0639\u0627\u0644\u06CC\u062A\u200C\u0647\u0627 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0646\u0634\u062F", "The activity could not be loaded");
      $[11] = t;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t) {
      t5 = t("\u0628\u0642\u06CC\u0647\u200C\u06CC \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0633\u0627\u0644\u0645 \u0627\u0633\u062A\u061B \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F.", "The rest of this page is fine \u2014 try again.");
      $[13] = t;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== refetch) {
      t6 = () => void refetch();
      $[15] = refetch;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <RotateCw aria-hidden={true} />;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== isFetching || $[19] !== t) {
      t8 = isFetching ? t("\u062F\u0631 \u062D\u0627\u0644 \u062A\u0644\u0627\u0634\u2026", "Retrying\u2026") : t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again");
      $[18] = isFetching;
      $[19] = t;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== isFetching || $[22] !== t6 || $[23] !== t8) {
      t9 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={t6} disabled={isFetching}>{t7}{t8}</button>;
      $[21] = isFetching;
      $[22] = t6;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t4 || $[26] !== t5 || $[27] !== t9) {
      t10 = <DeskEmpty tone="error" icon={t3} title={t4} body={t5} actions={t9} />;
      $[25] = t4;
      $[26] = t5;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    return t10;
  }
  if (activities.length === 0) {
    let t3;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <History />;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    let t4;
    if ($[30] !== t) {
      t4 = t("\u0647\u0646\u0648\u0632 \u0641\u0639\u0627\u0644\u06CC\u062A\u06CC \u062B\u0628\u062A \u0646\u0634\u062F\u0647", "No activity yet");
      $[30] = t;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    let t5;
    if ($[32] !== t) {
      t5 = t("\u0647\u0631 \u0645\u06CC\u0646\u062A\u060C \u0641\u0631\u0648\u0634 \u0648 \u0627\u0646\u062A\u0642\u0627\u0644\u06CC \u06A9\u0647 \u062B\u0628\u062A \u0634\u0648\u062F \u0627\u06CC\u0646\u062C\u0627 \u0645\u06CC\u200C\u0622\u06CC\u062F.", "Every recorded mint, sale and transfer appears here.");
      $[32] = t;
      $[33] = t5;
    } else {
      t5 = $[33];
    }
    let t6;
    if ($[34] !== t4 || $[35] !== t5) {
      t6 = <DeskEmpty icon={t3} title={t4} body={t5} />;
      $[34] = t4;
      $[35] = t5;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    return t6;
  }
  const days = [];
  for (const a of activities) {
    const key = dayKey(a.timestamp);
    const last = days[days.length - 1];
    if (last?.key === key) {
      last.items.push(a);
    } else {
      days.push({
        key,
        label: dayLabel(a.timestamp, locale, isFa),
        items: [a]
      });
    }
  }
  const t3 = "vt-stack";
  const t4 = days.map(day => <section key={day.key} className="vt-day" aria-label={day.label}><h3 className="vt-day-h"><SecGlyph className="vt-glyph" />{day.label}<span>{nf.format(day.items.length)}</span></h3><ol className="vt-events">{day.items.map((a_0, i) => {
        const row = readActivityRow(a_0);
        const kind = KIND[row.kind];
        return <li key={`${a_0.tx_hash}-${a_0.log_index}-${i}`} className="vt-ev" data-kind={row.kind === "mint" ? "mint" : "transfer"}><span className="vt-ev-mark" aria-hidden={true}><kind.Icon /></span><div className="vt-ev-body"><p className="vt-ev-head"><span className="vt-ev-kind">{isFa ? kind.fa : kind.en}</span>{a_0.marketplace ? <span className="vt-market ns-market">{a_0.marketplace}</span> : null}{row.amount != null ? <span className="ns-sale"><b>{row.amount.toLocaleString(locale, {
                    maximumFractionDigits: 6
                  })}</b>{row.symbol ? <small>{row.symbol}</small> : null}</span> : null}<time dateTime={a_0.timestamp}>{dateTime(a_0.timestamp, locale)}</time></p><p className="vt-ev-path">{row.kind === "mint" && !row.seller ? null : <><span className="v2-sr-only">{t("\u0641\u0631\u0648\u0634\u0646\u062F\u0647", "Seller")}</span><Who name={a_0.seller?.wallet?.user?.name} address={row.seller} /><Toward aria-hidden={true} className="vt-ev-arrow" /></>}<span className="v2-sr-only">{row.kind === "mint" ? t("\u0633\u0627\u0632\u0646\u062F\u0647", "Minted to") : t("\u062E\u0631\u06CC\u062F\u0627\u0631", "Buyer")}</span><Who name={a_0.buyer?.wallet?.user?.name} address={row.buyer} /></p></div>{row.tx ? <TxLink chain={a_0.chain} hash={row.tx} label={t("\u0645\u0634\u0627\u0647\u062F\u0647\u200C\u06CC \u062A\u0631\u0627\u06A9\u0646\u0634", "View transaction")} /> : <span aria-hidden={true} />}</li>;
      })}</ol></section>);
  let t5;
  if ($[37] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[37] = t4;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  return t5;
}

/** Who holds the token — the legacy tab's query and share-of-shown, as the collection page's ranked list. */
export function NftHolders(t0) {
  const $ = _c(42);
  const {
    contractAddress,
    tokenId,
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const locale = isFa ? "fa-IR" : "en-US";
  const nf = new Intl.NumberFormat(locale);
  let t2;
  if ($[2] !== contractAddress || $[3] !== lang || $[4] !== tokenId) {
    t2 = {
      contractAddress,
      tokenId,
      lang
    };
    $[2] = contractAddress;
    $[3] = lang;
    $[4] = tokenId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    holders,
    totalOwned,
    isLoading,
    isError,
    isFetching,
    refetch
  } = useNftHolders(t2);
  if (isLoading) {
    let t3;
    if ($[6] !== t) {
      t3 = t("\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u062F\u0627\u0631\u0646\u062F\u06AF\u0627\u0646", "Loading holders");
      $[6] = t;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t3) {
      t4 = <RowsSkeleton label={t3} rows={3} />;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (isError) {
    let t3;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <TriangleAlert />;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t) {
      t4 = t("\u062F\u0627\u0631\u0646\u062F\u06AF\u0627\u0646 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0646\u0634\u062F", "The holders could not be loaded");
      $[11] = t;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t) {
      t5 = t("\u0628\u0642\u06CC\u0647\u200C\u06CC \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0633\u0627\u0644\u0645 \u0627\u0633\u062A\u061B \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F.", "The rest of this page is fine \u2014 try again.");
      $[13] = t;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    let t6;
    if ($[15] !== refetch) {
      t6 = () => void refetch();
      $[15] = refetch;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <RotateCw aria-hidden={true} />;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== isFetching || $[19] !== t) {
      t8 = isFetching ? t("\u062F\u0631 \u062D\u0627\u0644 \u062A\u0644\u0627\u0634\u2026", "Retrying\u2026") : t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again");
      $[18] = isFetching;
      $[19] = t;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== isFetching || $[22] !== t6 || $[23] !== t8) {
      t9 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={t6} disabled={isFetching}>{t7}{t8}</button>;
      $[21] = isFetching;
      $[22] = t6;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t4 || $[26] !== t5 || $[27] !== t9) {
      t10 = <DeskEmpty tone="error" icon={t3} title={t4} body={t5} actions={t9} />;
      $[25] = t4;
      $[26] = t5;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    return t10;
  }
  if (holders.length === 0) {
    let t3;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Users />;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    let t4;
    if ($[30] !== t) {
      t4 = t("\u0647\u0646\u0648\u0632 \u062F\u0627\u0631\u0646\u062F\u0647\u200C\u0627\u06CC \u062B\u0628\u062A \u0646\u0634\u062F\u0647", "No holders recorded");
      $[30] = t;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    let t5;
    if ($[32] !== t) {
      t5 = t("\u0645\u0627\u0644\u06A9\u06CC\u062A \u0627\u06CC\u0646 \u0627\u062B\u0631 \u0648\u0642\u062A\u06CC \u062B\u0628\u062A \u0634\u0648\u062F \u0627\u06CC\u0646\u062C\u0627 \u0645\u06CC\u200C\u0622\u06CC\u062F.", "Ownership appears here once it is recorded.");
      $[32] = t;
      $[33] = t5;
    } else {
      t5 = $[33];
    }
    let t6;
    if ($[34] !== t4 || $[35] !== t5) {
      t6 = <DeskEmpty icon={t3} title={t4} body={t5} />;
      $[34] = t4;
      $[35] = t5;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    return t6;
  }
  const t3 = "vt-ranks ns-ranks";
  let t4;
  if ($[37] !== t) {
    t4 = t("\u062F\u0627\u0631\u0646\u062F\u06AF\u0627\u0646", "Holders");
    $[37] = t;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  const t5 = holders.map((h, i) => {
    const share = totalOwned > 0 ? h.balance / totalOwned * 100 : 0;
    return <li key={`${h.wallet_address}-${i}`} className="vt-rank" data-top={i === 0 || undefined}><span className="vt-rank-no">{nf.format(i + 1)}</span><span className="vt-rank-who">{h.wallet?.user?.name ? <b>{h.wallet.user.name}</b> : null}<FitHex value={h.wallet_address} /></span><span className="vt-rank-owned"><b>{h.balance.toLocaleString(locale)}</b></span><span className="vt-rank-share"><span className="vt-bar" aria-hidden={true}><i style={{
            inlineSize: `${Math.min(100, Math.max(share, share > 0 ? 3 : 0))}%`
          }} /></span><span>{withPercent(share.toLocaleString(locale, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
          }), isFa)}</span></span></li>;
  });
  let t6;
  if ($[39] !== t4 || $[40] !== t5) {
    t6 = <ol className={t3} aria-label={t4}>{t5}</ol>;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  return t6;
}